import * as R from 'ramda'
import {InquiryDetailsState} from './InquiryDetailsState'
import {InquiryDetailsAction} from './InquiryDetailsActions'
import {
    InquiryDetailsDownloadRequestIdentifier,
    InquiryDetailsFeedback,
    InquiryDetailsRequestIdentifier
} from './InquiryDetailsFeedback'
import {HttpError} from '../../Data/Http'
import * as Result from '../../Data/Result'
import * as InquiryResource from '../../Request/InquiryResource'
import * as InquiryWithdetails from '../../Entities/InquiryWithDetails'
import {InquiryWithDetails} from '../../Entities/InquiryWithDetails'
import {update} from '../../Data/ObjectHelper'
import {
    initOverview,
    overviewDownloadResponseAction,
    overviewReducer,
    overviewResponseAction
} from './Overview/OverviewReducer'
import {
    initOrder,
    orderReducer,
    orderResponseAction
} from './Order/OrderReducer'
import {
    initOffer,
    offerDownloadResponseAction,
    offerReducer,
    offerResponseAction
} from './Offer/OfferReducer'
import {scrollDivDown} from '../../Data/Dom'
import {InquiryHistoryEntryWithUsers} from '../../Entities/InquiryHistoryEntryWithUsers'
import {Session} from '../../Data/Session'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {OverviewFeedback} from './Overview/OverviewFeedback'
import {HttpRequest} from '../../Data/HttpRequest'
import {Decoder} from 'json-bouncer'

export function activate(session: Session, inquiryNumber: number): HttpRequest {
    return InquiryResource.retrieve(session.manufacturer.id, inquiryNumber)
}

export function initialDecoder(): Decoder<InquiryWithdetails.InquiryWithDetails> {
    return InquiryWithdetails.decoder()
}

export function init(inquiryWithDetails: InquiryWithDetails): InquiryDetailsLoop {
    return {
        state: {
            inquiryWithDetails,
            overviewTab: initOverview(inquiryWithDetails),
            offerTab: initOffer(inquiryWithDetails),
            orderTab: initOrder(inquiryWithDetails),
            activeTab: 'overview',
        },
        feedback: [{
            type: 'RunEffect',
            effect:
                scrollDivDown<InquiryDetailsAction>(
                    'history-scroll-view',
                    {type: 'NoOp'},
                ),
        }]
    }
}

type InquiryDetailsLoop =
    FeedbackLoop<InquiryDetailsState, InquiryDetailsFeedback>

export function reduce(
    session: Session,
    state: InquiryDetailsState,
    action: InquiryDetailsAction
): InquiryDetailsLoop {
    switch (action.type) {
        case 'ActiveTabChanged':
            return {
                state: update({activeTab: action.value}, state),
                feedback: []
            }
        case 'MenuButtonClicked': {
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        }
        case 'OfferTabAction': {
            const
                loop = offerReducer(
                    session,
                    state.inquiryWithDetails.inquiry.inquiryNumber,
                    action.subAction,
                    state.offerTab,
                ),
                wrappedLoop = {
                    state: update({offerTab: loop.state}, state),
                    feedback: [],
                }
            return R.reduce(
                (previousLoop, offerFeedback) => {
                    switch (offerFeedback.type) {
                        case 'SyncPositions':
                            // TODO
                            // tslint:disable-next-line
                            console.log('Sync positions')
                            return previousLoop
                        case 'SendRequest': {
                            const wrappedFeedback: InquiryDetailsFeedback = {
                                type: 'SendRequest',
                                identifier: {
                                    type: 'OfferRequest',
                                    identifier: offerFeedback.identifier,
                                },
                                httpRequest: offerFeedback.httpRequest,
                            }
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append(
                                        wrappedFeedback,
                                        previousLoop.feedback,
                                    ),
                            }
                        }
                        case 'SendDownloadRequest': {
                            const wrappedFeedback: InquiryDetailsFeedback = {
                                type: 'SendDownloadRequest',
                                identifier: {
                                    type: 'OfferDownloadRequest',
                                    identifier: offerFeedback.identifier,
                                },
                                httpRequest: offerFeedback.httpRequest,
                                filename: offerFeedback.filename,
                            }
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append(
                                        wrappedFeedback,
                                        previousLoop.feedback,
                                    )
                            }
                        }
                        case 'UpdateHistoryAfterOfferCreation': {
                            const statusChange: InquiryHistoryEntryWithUsers = {
                                type: 'StatusChange',
                                statusChange: offerFeedback.inquiryStatusChangeWithUsers,
                            }
                            return {
                                state: update(
                                    {
                                        activeTab: 'overview',
                                        inquiryWithDetails: update(
                                            {inquiry: offerFeedback.inquiry},
                                            previousLoop.state.inquiryWithDetails
                                        ),
                                        overviewTab: update(
                                            {
                                                history: R.append(
                                                    statusChange,
                                                    previousLoop.state.overviewTab.history,
                                                ),
                                            },
                                            previousLoop.state.overviewTab)
                                    },
                                    previousLoop.state
                                ),
                                feedback:
                                    R.append(
                                        {
                                            type: 'RunEffect',
                                            effect: scrollDivDown('history-scroll-view', {type: 'NoOp'}),
                                        },
                                        previousLoop.feedback,
                                    ),
                            }
                        }
                        case 'UpdateTotalsAfterPositionSave': {
                            return {
                                state:
                                    update(
                                        {
                                            inquiryWithDetails:
                                                update(
                                                    {
                                                        totals: offerFeedback.totals
                                                    },
                                                    previousLoop.state.inquiryWithDetails,
                                                ),
                                        },
                                        previousLoop.state,
                                    ),
                                feedback: previousLoop.feedback,
                            }
                        }
                    }
                },
                wrappedLoop,
                loop.feedback)
        }
        case 'OverviewTabAction': {
            const
                loop =
                    overviewReducer(
                        session,
                        state.inquiryWithDetails.inquiry.inquiryNumber,
                        action.subAction,
                        state.overviewTab
                    ),
                wrappedLoop = {
                    state: update({overviewTab: loop.state}, state),
                    feedback: [],
                }
            return R.reduce<OverviewFeedback, InquiryDetailsLoop>(
                (previousLoop, overviewFeedback) => {
                    switch (overviewFeedback.type) {
                        case 'RunEffect':
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append<InquiryDetailsFeedback>(
                                        {
                                            type: 'RunEffect',
                                            effect:
                                                overviewFeedback.effect.map(
                                                    (subAction): InquiryDetailsAction => ({
                                                        type: 'OverviewTabAction',
                                                        subAction,
                                                    })
                                                ),
                                        },
                                        previousLoop.feedback,
                                    ),
                            }
                        case 'SendRequest': {
                            const wrappedFeedback: InquiryDetailsFeedback = {
                                type: 'SendRequest',
                                identifier: {
                                    type: 'OverviewRequest',
                                    identifier: overviewFeedback.identifier,
                                },
                                httpRequest: overviewFeedback.httpRequest,
                            }
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append(
                                        wrappedFeedback,
                                        previousLoop.feedback,
                                    ),
                            }
                        }
                        case 'SendDownloadRequest': {
                            const wrappedFeedback: InquiryDetailsFeedback = {
                                type: 'SendDownloadRequest',
                                identifier: {
                                    type: 'OverviewDownloadRequest',
                                    identifier: overviewFeedback.identifier,
                                },
                                httpRequest: overviewFeedback.httpRequest,
                                filename: overviewFeedback.filename,
                            }
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append(
                                        wrappedFeedback,
                                        previousLoop.feedback,
                                    ),
                            }
                        }
                    }
                },
                wrappedLoop,
                loop.feedback)
        }
        case 'OrderTabAction': {
            const
                orderLoop =
                    orderReducer(
                        session,
                        state.inquiryWithDetails.inquiry.inquiryNumber,
                        action.subAction,
                        state.orderTab
                    ),
                wrappedLoop = {
                    state: update({orderTab: orderLoop.state}, state),
                    feedback: [],
                }
            return R.reduce(
                (previousLoop, orderFeedback) => {
                    switch (orderFeedback.type) {
                        case 'SendRequest': {
                            const wrappedFeedback: InquiryDetailsFeedback = {
                                type: 'SendRequest',
                                identifier: {
                                    type: 'OrderRequest',
                                    identifier: orderFeedback.identifier,
                                },
                                httpRequest: orderFeedback.httpRequest,
                            }
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append(
                                        wrappedFeedback,
                                        previousLoop.feedback,
                                    ),
                            }
                        }
                    }
                },
                wrappedLoop,
                orderLoop.feedback)
        }
        case 'NoOp':
            return {state, feedback: []}
    }
}

export function httpResponseAction(
    identifier: InquiryDetailsRequestIdentifier,
    result: Result.Result<HttpError, string>
): InquiryDetailsAction {
    switch (identifier.type) {
        case 'OfferRequest':
            return {
                type: 'OfferTabAction',
                subAction: offerResponseAction(identifier.identifier, result),
            }
        case 'OverviewRequest':
            return {
                type: 'OverviewTabAction',
                subAction: overviewResponseAction(identifier.identifier, result),
            }
        case 'OrderRequest':
            return {
                type: 'OrderTabAction',
                subAction: orderResponseAction(identifier.identifier, result),
            }

    }
}

export function httpDownloadResponseAction(
    identifier: InquiryDetailsDownloadRequestIdentifier,
    result: Result.Result<HttpError, null>
): InquiryDetailsAction {
    switch (identifier.type) {
        case 'OfferDownloadRequest':
            return {
                type: 'OfferTabAction',
                subAction:
                    offerDownloadResponseAction(identifier.identifier, result),
            }
        case 'OverviewDownloadRequest':
            return {
                type: 'OverviewTabAction',
                subAction:
                    overviewDownloadResponseAction(
                        identifier.identifier,
                        result,
                    ),
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/InquiryDetails/InquiryDetailsReducer.ts